import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, Film, X } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { RED_API, fmtDate, useMeta } from "../components/red/api";
import { Select, Spinner, RecreacionBadge } from "../components/red/ui";

const fileUrl = (c, extra = "") => `${RED_API}/public/contenidos/${c.id}/archivo${extra}`;

export default function BibliotecaPublica() {
  const [activa, setActiva] = useState(null);
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [prov, setProv] = useState("");
  const [open, setOpen] = useState(null);
  const [meta] = useMeta();

  useEffect(() => { axios.get(`${RED_API}/public/estado`).then((r) => setActiva(r.data.activa)).catch(() => setActiva(false)); }, []);
  useEffect(() => {
    if (activa) axios.get(`${RED_API}/public/contenidos`, { params: { categoria: cat, provincia: prov } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [activa, cat, prov]);

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="biblioteca-publica">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between gap-4">
            <Link to="/"><LogoMark className="h-9" variant="light" /></Link>
            <Link to="/" className="text-sm text-white/70 hover:text-[#F5B400] font-headings uppercase tracking-wide" data-testid="biblio-pub-volver">← Volver al sitio</Link>
          </div>
          <div className="mt-8 font-headings uppercase tracking-[0.3em] text-xs text-[#F5B400]">Red Nacional de Contenidos</div>
          <h1 className="font-headings uppercase font-bold text-4xl sm:text-5xl lg:text-6xl mt-2">Biblioteca Frente Ya</h1>
          <p className="text-white/70 mt-3 max-w-xl">Videos, fotos y afiches de todo el país. Descargalos y compartilos.</p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">
        {activa === null ? <Spinner /> : !activa ? (
          <div className="bg-white border p-10 text-center text-gray-500" data-testid="biblioteca-no-disponible">La biblioteca pública todavía no está disponible.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-xl">
              <Select value={cat} placeholder="Todas las categorías" options={meta.categorias} onChange={(e) => setCat(e.target.value)} data-testid="pub-categoria" />
              <Select value={prov} placeholder="Todas las provincias" options={meta.provincias} onChange={(e) => setProv(e.target.value)} data-testid="pub-provincia" />
            </div>
            {items.length === 0 ? <div className="bg-white border p-10 text-center text-gray-500">No hay contenidos publicados con esos filtros.</div> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((c) => (
                  <div key={c.id} className="bg-white border border-gray-200 flex flex-col" data-testid={`pub-item-${c.id}`}>
                    <button onClick={() => setOpen(c)} className="aspect-video bg-[#0A192F] overflow-hidden">
                      {c.thumb_file_id ? <img src={fileUrl(c, "?thumb=1")} alt={c.titulo} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-[#F5B400]"><Film size={36} /></div>}
                    </button>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex gap-2 flex-wrap mb-1"><span className="text-[10px] font-headings uppercase tracking-wider text-[#0A2463]">{c.categoria}</span>{c.es_recreacion && <RecreacionBadge />}</div>
                      <h3 className="font-semibold text-[#0A192F]">{c.titulo}</h3>
                      <p className="text-xs text-gray-500 mt-1">{c.localidad}, {c.provincia} · {fmtDate(c.fecha)}</p>
                      <a href={fileUrl(c, "?dl=1")} className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm font-headings uppercase text-[#0A2463]" data-testid={`pub-descargar-${c.id}`}><Download size={15} /> Descargar</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-3" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between p-3 border-b"><h3 className="font-headings uppercase">{open.titulo}</h3><button onClick={() => setOpen(null)}><X /></button></div>
            {open.tipo === "video" ? <video src={fileUrl(open)} controls playsInline className="w-full max-h-[65vh] bg-black" /> : <img src={fileUrl(open)} alt={open.titulo} className="w-full max-h-[65vh] object-contain bg-black" />}
            <p className="p-4 text-sm text-[#374151] whitespace-pre-line">{open.descripcion}{open.es_recreacion ? "\n\nEsta imagen es una recreación digital." : ""}</p>
          </div>
        </div>
      )}
    </div>
  );
}
